/**
 * Maps raw solver constraint rule names to user-friendly descriptions.
 *
 * The solver engine produces technical rule names (see SolverRuleNames.java).
 * This lookup provides plain-English translations suitable for end-user display
 * in tooltips, alert panels, and notification dialogs.
 */
const SOLVER_RULE_FRIENDLY: Record<string, string> = {
  // Hard constraints
  "Resources must only work within assigned zones":
    "The assigned operative is not permitted to work in this zone.",
  "Resources must only be assigned work for which they have the skills":
    "The assigned operative does not have the required skills for this ticket.",
  "The schedule must be feasible":
    "The schedule is not feasible — there may be overlapping or impossible assignments.",
  "Appointments must be scheduled for the correct day and not the day ahead":
    "This ticket has been scheduled a day too early.",
  "Appointments must be scheduled for the correct day and not the day behind":
    "This ticket has been scheduled a day too late.",
  "Wait time on ticket must not be too excessive":
    "There is an excessive wait time before this ticket.",

  // Medium constraints
  "Manually locked tickets must only be done by the locked resource":
    "This ticket is locked to a specific operative who is not currently assigned.",
  "Resources must not be forbidden to work on a property by a rule":
    "The assigned operative is restricted from working at this property.",
  "Resources must be permitted to work together":
    "The operatives assigned to this group of work are not permitted to work together.",
  "Changes in the near future should be avoided where possible":
    "A schedule change has been made close to the start time, which may cause disruption.",
  "Tickets should not overrun resource availability":
    "This ticket is expected to overrun the operative's available shift time.",
  "Tickets must not overrun appointment":
    "This ticket starts or finishes outside the booked appointment window.",
  "Tickets must not start before appointment":
    "This ticket is scheduled to start before the booked appointment window.",
  "Low Priority Reservation Exceeded":
    "The low-priority reservation capacity for this shift has been exceeded.",
  "Normal Priority Reservation Exceeded":
    "The normal-priority reservation capacity for this shift has been exceeded.",
  "High Priority Reservation Exceeded":
    "The high-priority reservation capacity for this shift has been exceeded.",
  "Emergency Priority Reservation Exceeded":
    "The emergency-priority reservation capacity for this shift has been exceeded.",

  // Soft constraints
  "The total travel time between tickets should be minimised":
    "There is significant travel time between tickets.",
  "The total waiting time between tickets should be minimised":
    "There is significant waiting time between tickets.",
  "Minimise end of shift travel":
    "The operative has a long return-to-base journey at the end of their shift.",
  "The forced start time value requested should be honoured":
    "The requested start time cannot be honoured, either due to in-progress work, operative availability or another ticket with a requested start time.",
  "Normal Priority Reservation Use": "Normal-priority reservation in use.",
  "High Priority Reservation Use": "High-priority reservation in use.",
  "Emergency Priority Reservation Use":
    "Emergency-priority reservation in use.",
  "Follow on required":
    "A follow-on visit is required but has not been scheduled.",
  "Priority Ordering": "Ticket priority ordering could not be fully respected.",
  "Minimise resource cost": "A lower-cost operative may be available.",
  "The schedule should be updated according to the current time":
    "The schedule needs to be updated to reflect the current time."
};

/**
 * Return a user-friendly translation for a solver rule name.
 * Falls back to the original rule string when no translation exists.
 */
export function translateSolverRule(rule: string): string {
  return SOLVER_RULE_FRIENDLY[rule] ?? rule;
}
